import React from 'react';
import _ from 'underscore';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

/**
 * Popover component provides an input field for searching through content
 */

export class Popover extends React.Component {
  static defaultProps = {
    trigger: 'click',
    keepTargetPropagation: false,
    outsideClickDismiss: true,
    placement: 'auto',
    defaultVisible: false,
    onOpen: () => {},
    onDismiss: () => {},
    isOpen: false,
    customKeepOpen: () => false,
    ariaLabel: null,
  };

  state = {
    top: 0,
    left: 0,
    open: this.props.isOpen,
    finishedRendering: false,
    isPositionSet: false,
  };

  // As the comment above componentDidUpdate mentioned, we have to render it first
  // with hidden visibility and then call the setstate.
  componentDidMount() {
    if (this.props.defaultVisible) {
      /* eslint-disable react/no-did-mount-set-state */
      this.setState({ open: true });
    }
    if (this.props.isOpen) {
      this.subscribeToPageClick();
    }
    this.setState({
      finishedRendering: true,
    });
  }

  // FinishedRendering flag will make sure the render function has run at least once
  // and generated this.targetElement. Once it exists, now it will append the popover children
  // underneath the target element.
  // Now, everytime a state changes, (most likely open), it will re-calculate the style property
  // and attach to the target element dom as react element.
  componentDidUpdate() {
    if (this.targetElement && this.state.finishedRendering) {
      if (this.state.open) {
        const popover = (
          <div
            className="bingads-popover"
            ref={(c) => { this.popover = c; }}
            style={{
              position: 'absolute',
              zIndex: 5000,
              visibility: 'visible',
              top: _.isFinite(this.state.top) ? `${this.state.top}px` : null,
              left: _.isFinite(this.state.left) ? `${this.state.left}px` : null,
              right: _.isFinite(this.state.right) ? `${this.state.right}px` : null,
              bottom: _.isFinite(this.state.bottom) ? `${this.state.bottom}px` : null,
            }}
          >
            {this.props.children}
          </div>
        );
        let placeholderExists = false;

        const targetChildren = this.targetElement.firstElementChild.children;
        if (targetChildren) {
          placeholderExists = _.some(targetChildren, child => child.className === 'bingads-popover-placeholder');
        }

        if (!placeholderExists) {
          const newPlaceholderElem = document.createElement('div');
          newPlaceholderElem.className = 'bingads-popover-placeholder';
          this.targetElement.firstElementChild.appendChild(newPlaceholderElem);
        }

        ReactDOM.render(
          popover,
          _.find(this.targetElement.firstElementChild.children, item => item.className === 'bingads-popover-placeholder'),
          () => {
            if (!this.state.isPositionSet) {
              this.calculatePosition(this.props.placement, this);
            }
          }
        );
        return;
      }
      if (this.props.onDismiss) {
        this.props.onDismiss();
      }
      const popoverPlaceholder = _.find(
        this.targetElement.firstElementChild.children,
        item => item.className === 'bingads-popover-placeholder'
      );
      if (this.targetElement && this.targetElement.firstElementChild &&
        popoverPlaceholder) {
        ReactDOM.unmountComponentAtNode(popoverPlaceholder);
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribeToPageClick();
  }

  calculatePosition = (method, childElem) => {
    const target = this.targetContainer.firstElementChild.firstElementChild.getBoundingClientRect();

    switch (method) {
      case 'bottom':
        this.setState({
          top: target.height + 10,
          left: 0,
          right: null,
          bottom: null,
          isPositionSet: true,
        });
        break;
      case 'top':
        // 10px + height of target element + height of popover element
        this.setState({
          top: null,
          right: null,
          bottom: target.height + 10,
          left: 0,
          isPositionSet: true,
        });
        break;
      case 'left':
        // 10px + width of target element
        this.setState({
          top: 0,
          right: childElem.popover.firstElementChild.offsetWidth + target.width + 10,
          left: null,
          bottom: null,
          isPositionSet: true,
        });
        break;
      case 'right':
        // 10px + width of target element
        this.setState({
          top: 0,
          left: target.width + 10,
          bottom: null,
          right: null,
          isPositionSet: true,
        });
        break;
      case 'auto': {
        // right by default. left if not enough space.
        if ((target.left + target.width +
            childElem.popover.firstElementChild.offsetWidth + 10)
            > window.innerWidth) {
          // position to left
          this.calculatePosition('left', childElem);
        } else {
          // position to right
          this.calculatePosition('right', childElem);
        }
        break;
      }
      default:
        // if none of the above, set it to auto.
        this.calculatePosition('auto', childElem);
    }
  }

  togglePopover = (e) => {
    if (e.stopPropagation && !this.props.keepTargetPropagation) {
      e.stopPropagation();
    }

    if (e.type !== this.props.trigger) {
      return;
    }

    // if popover content is clicked, do nothing
    if (this.popover && this.popover.contains(e.target)) {
      return;
    }
    if (!this.state.open) {
      this.subscribeToPageClick();
      if (this.props.onOpen) {
        this.props.onOpen();
      }
    } else {
      this.unsubscribeToPageClick();
      if (this.props.onDismiss) {
        this.props.onDismiss();
      }
    }

    // Get dimension of target element to properly position popover
    this.setState(prevState => ({
      open: !prevState.open,
    }));
  }

  dismissPopover = () => {
    this.setState({
      open: false,
    });
  }

  handleClick = ({ target }) => {
    if (!this.props.outsideClickDismiss ||
        target.id === this.props.target.props.id ||
        (this.popover && this.popover.contains(target)) ||
        this.props.customKeepOpen({ target })) {
      return;
    }

    this.dismissPopover();
  }

  subscribeToPageClick() {
    document.addEventListener('click', this.handleClick, false);
  }

  unsubscribeToPageClick() {
    document.removeEventListener('click', this.handleClick, false);
  }

  render() {
    if (!this.state.open) {
      this.unsubscribeToPageClick();
    }

    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/interactive-supports-focus */
    return (
      <div className="bingads-popover-wrapper">
        <div
          aria-label={this.props.ariaLabel}
          className="target-wrapper"

          /*
            Event handlers here are for events bubbled up from target.
            So here we use presentation role.
            If target is non-clickable, then need support button role & tabindex="0"
          */
          role="presentation"

          ref={(c) => { this.targetContainer = c; }}
          onClick={this.togglePopover}
          onMouseOver={this.togglePopover}
          onFocus={this.togglePopover}
        >
          <div ref={(c) => { this.targetElement = c; }}>
            {this.props.target}
          </div>
        </div>
      </div>
    );
  }
}

Popover.propTypes = {
  // trigger: click, mouseover
  trigger: PropTypes.string,
  // target: target button or element. Needs to be react element
  target: PropTypes.node.isRequired,
  // top, left, right, bottom
  placement: PropTypes.oneOf(['auto', 'top', 'left', 'right', 'bottom']),
  // boolean value which provides option to dismiss popover when clicked outside
  outsideClickDismiss: PropTypes.bool,
  // boolean value which provides option to keep events on target still propagate
  // this option is useful when clicking target should close other opened popovers
  keepTargetPropagation: PropTypes.bool,
  // Whether the popover is visible by default
  defaultVisible: PropTypes.bool,
  // customized function to keep popover open when clicked
  customKeepOpen: PropTypes.func,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onDismiss: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  ariaLabel: PropTypes.string,
};

